/**
 * What each screen needs, any one of it.
 *
 * The menu only ever showed doors that open, and that was the whole of it: an address typed or
 * bookmarked, a link on another screen or a lesson opened any screen for anybody signed in, so a
 * cashier who typed `/precios` could change prices, receive stock or count it. The router now
 * checks this before it shows a screen (`Raiz` in `router.tsx`), whatever brought somebody
 * there. A tab's entry is the same as its tab in `secciones.ts`, so the menu and the door never
 * disagree; `permisos.test.ts` holds both to that, and holds every route to having a line here.
 *
 * An empty list is a screen for everybody signed in. The events themselves are checked again by
 * the reducer, which is what a device that skips the screens runs into.
 */

import type { Permission } from '@abastece/shared';

export const PERMISOS_DE_PANTALLA: Readonly<Record<string, readonly Permission[]>> = {
  '/': [],
  '/mas': [],
  '/aprender': [],
  '/ajustes': [],
  '/ajustes/negocio': ['configurar'],
  '/ajustes/personal': ['gestionar_usuarios'],

  '/pos': ['vender'],
  '/caja': ['abrir_caja'],
  '/caja/empezar': ['abrir_caja'],
  '/caja/terminar': ['cerrar_caja'],
  '/caja/recibir': ['abrir_caja'],
  '/caja/turnos': ['abrir_caja'],
  '/ventas': ['vender'],
  '/transferencias': ['confirmar_transferencia'],
  '/recargas': ['vender'],
  '/recargas/anteriores': ['vender'],

  '/productos': ['editar_productos'],
  '/productos/nuevo': ['editar_productos'],
  '/productos/$productoId': ['editar_productos'],
  '/productos/semilla': ['editar_productos'],
  '/carga-rapida': ['editar_productos'],
  // What the products sell for: the owner's (2026-10-13), where the products are a cashier's too.
  '/precios': ['editar_precios'],
  '/listas-precio': ['editar_precios'],
  // The products still without a photo (2026-10-20): whoever gives a product its photo on its page.
  '/fotos': ['editar_productos'],
  // The pack sizes the sales ask for (2026-10-21): whoever adds a presentation on a product's page.
  '/paquetes': ['editar_productos'],
  // What to put on sale, and what is (2026-11-03): an offer is a price, for some days.
  '/ofertas': ['editar_precios'],

  '/inventario': ['ajustar_inventario'],
  '/inventario/conteo': ['ajustar_inventario'],
  '/inventario/reempaque': ['reempacar'],
  '/inventario/traslado': ['ajustar_inventario'],
  '/vencimientos': ['ajustar_inventario'],

  '/reponer': ['recibir_compras'],
  '/compras': ['recibir_compras'],
  '/compras/nueva': ['recibir_compras'],
  '/proveedores': ['recibir_compras'],
  '/proveedores/$proveedorId': ['recibir_compras'],

  '/clientes': ['gestionar_clientes'],
  '/clientes/$clienteId': ['gestionar_clientes'],
  '/clientes/pedidos/nuevo': ['gestionar_clientes'],
  '/clientes/pedidos/$pedidoId/editar': ['gestionar_clientes'],
  // What the clients owe, all of it: whoever looks after the clients, as each one's account is (the
  // owner, 2026-10-08, put it on the cashier's home screen). A limit stays the owner's to set.
  '/fiado': ['gestionar_clientes'],
  '/pedidos': ['gestionar_clientes'],

  '/contabilidad': ['ver_reportes'],
  '/analisis': ['ver_reportes'],
  '/control': ['ver_reportes'],
  '/control/$usuarioId': ['ver_reportes'],
  '/efectivo': ['recibir_efectivo'],
  '/efectivo/depositos': ['recibir_efectivo'],
  // The owner going over the day's money: what the tills handed over, the transfers, the bank.
  '/dinero/semana': ['recibir_efectivo'],
  '/gastos': ['ver_reportes'],
  '/gastos/pagos': ['ver_reportes'],
  '/gastos/dueno': ['ver_reportes'],
  '/planilla': ['gestionar_planilla'],
  '/exportar': ['ver_reportes'],
  '/bitacora': ['ver_bitacora'],
};

/**
 * Whether somebody may see the screen a route id names. A route with no line here is refused:
 * a screen added without deciding who it is for is closed until somebody decides.
 */
export function pantallaPermitida(ruta: string, puede: (p: Permission) => boolean): boolean {
  const permisos = PERMISOS_DE_PANTALLA[ruta];
  if (permisos === undefined) return false;
  return permisos.length === 0 || permisos.some(puede);
}
